import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    UIButtonComponent,
    UINewThemeService,
    UINotificationService,
    UITooltipDirective
} from '@bannerflow/ui';
import { CreativesetDataService } from '@studio/common/creativeSet';
import { EnvironmentService } from '@studio/common/services/environment.service';
import { FollowCreativeSetService } from '../state';

const HASH_UNFOLLOW_PREFIX = '#unfollow=';

@Component({
    imports: [UIButtonComponent, UITooltipDirective],
    selector: 'lib-follow-creativeset-toggle',
    template: `
        @if (showButton()) {
            @if (isNewUI()) {
                <ui-button
                    nuiType="plain-primary"
                    nuiSvgIcon="visibility"
                    [id]="gainsightId()"
                    [selected]="isFollowing()"
                    [loading]="!loaded()"
                    [uiTooltip]="tooltipText()"
                    [uiTooltipPosition]="'bottom'"
                    [uiTooltipMaxWidth]="'206px'"
                    (click)="handleButtonClicked()" />
            } @else {
                <ui-button
                    svgIcon="visibility-visible"
                    [id]="gainsightId()"
                    [type]="buttonType()"
                    [uiTooltip]="tooltipText()"
                    [uiTooltipPosition]="'bottom'"
                    [uiTooltipMaxWidth]="'206px'"
                    [loading]="!loaded()"
                    (click)="handleButtonClicked()" />
            }
        }
    `,
    styles: ``
})
export class FollowCreativeSetToggleComponent {
    private creativeSetDataService = inject(CreativesetDataService);
    private environmentService = inject(EnvironmentService);
    private followCreativeSetService = inject(FollowCreativeSetService);
    private uiNewThemeService = inject(UINewThemeService);
    private uiNotificationService = inject(UINotificationService);

    protected isNewUI = this.uiNewThemeService.isNewThemeEnabled;
    protected isFollowing = toSignal(this.followCreativeSetService.isFollower$, {
        initialValue: false
    });
    protected loaded = toSignal(this.followCreativeSetService.loaded$, { initialValue: false });

    protected showButton = computed(() => !this.inShowcase() || (this.userEmail() && this.userName()));

    protected tooltipText = computed(() =>
        this.isFollowing()
            ? 'Click to stop following all the threads in this creative set'
            : 'Click to follow all the threads in this creative set'
    );
    protected buttonType = computed(() => (this.isFollowing() ? 'default' : 'discrete'));

    protected gainsightId = computed(() => {
        const type = this.inShowcase() ? 'showcase' : 'internal';
        const toggle = this.isFollowing() ? 'unfollow' : 'follow';
        return `creativeset-${toggle}-${type}`;
    });

    private inShowcase = toSignal(this.environmentService.inShowcaseMode$);

    private userEmail = toSignal(this.followCreativeSetService.userEmail$);
    private userName = toSignal(this.followCreativeSetService.userName$);

    private error = toSignal(this.followCreativeSetService.error$);
    private hashData = signal(window.location.hash);

    constructor() {
        effect(() => {
            const inShowcase = this.inShowcase();
            if (typeof inShowcase === 'undefined') {
                return;
            }

            if (!inShowcase) {
                this.followCreativeSetService.loadIsFollower(
                    +this.creativeSetDataService.creativeset.id
                );
                return;
            }

            this.followCreativeSetService.showcaseInitState(
                +this.creativeSetDataService.creativeset.id
            );
        });

        effect(() => {
            const error = this.error();
            if (error) {
                this.uiNotificationService.open('Something went wrong. Try again', {
                    placement: 'top',
                    type: 'error'
                });
            }
        });

        window.addEventListener('hashchange', () => {
            this.hashData.set(window.location.hash);
        });

        effect(() => {
            const creativeSetId = +this.creativeSetDataService.creativeset.id;
            const hashData = this.hashData();
            const inShowcase = this.inShowcase();
            if (!inShowcase || !hashData || !hashData.startsWith(HASH_UNFOLLOW_PREFIX)) {
                return;
            }
            const encodedUserEmail = hashData.split(HASH_UNFOLLOW_PREFIX)[1];
            if (!encodedUserEmail) {
                return;
            }
            const userEmail = atob(encodedUserEmail);
            if (userEmail) {
                this.followCreativeSetService.showcaseUnfollowCreativeSet(creativeSetId, userEmail);
                // optimistic rendering
                this.uiNotificationService.open('Successfully unfollowed creative set', {
                    placement: 'top',
                    type: 'success'
                });
                window.location.hash = '';
            }
        });
    }

    protected handleButtonClicked(): void {
        if (this.inShowcase()) {
            this.toggleShowcaseFollow();
            return;
        }
        this.toggleFollow();
    }

    private toggleFollow(): void {
        if (this.isFollowing()) {
            this.followCreativeSetService.unfollowCreativeSet(
                +this.creativeSetDataService.creativeset.id
            );
            return;
        }
        this.followCreativeSetService.followCreativeSet(+this.creativeSetDataService.creativeset.id);
    }

    private toggleShowcaseFollow(): void {
        const userEmail = this.userEmail();
        const userName = this.userName();

        if (this.isFollowing()) {
            if (userEmail) {
                this.followCreativeSetService.showcaseUnfollowCreativeSet(
                    +this.creativeSetDataService.creativeset.id,
                    userEmail
                );
            }
            return;
        }
        if (userEmail && userName) {
            this.followCreativeSetService.showcaseFollowCreativeSet(
                +this.creativeSetDataService.creativeset.id,
                userEmail,
                userName
            );
        }
    }
}
